import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { Dayjs } from 'dayjs';
import { Alert, Box, Card, Grid, Stack, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import Header from 'components/Common/Header';
import CardWrapper from 'components/Common/CardWrapper';
import Table from 'components/Common/StyledTable';
import Tab from 'components/Common/Tab';
import Button from 'components/Common/Button';
import MuiLoader from 'components/Common/Loader';
import { hasAtLeastRole } from 'api/v1Helpers';
import { dateFormat, formatAmount, timeFormat } from 'utils/helpers';
import { selectEffectiveStaffRole, selectSelectedParishId } from 'redux/selectors/parishContext';
import { AppDispatch, RootState } from 'redux/store';
import { updateTableState } from 'redux/slices/common/appSlice';
import { clearReports, fetchReportTransactions, fetchReportsSummary } from 'redux/slices/reports/reportsSlice';
import type { LedgerEntryRow, LedgerKind } from 'types/reports';

type TabValue = 'TRANSACTIONS' | 'SUMMARY';

const KIND_LABELS: Record<LedgerKind, string> = {
    DONATION: 'Donations',
    REFUND: 'Refunds',
    DISPUTE: 'Disputes (bank reversal)',
    FEE: 'Fees',
    STRIPE_FEE: 'Processing Fees',
    TRANSFER_IN: 'Transfers In',
    TRANSFER_OUT: 'Transfers Out',
    PAYOUT: 'Payouts',
};

/** REFUND/FEE ledger rows carry negative amountCents — formatAmount alone renders "$-25.00". */
function formatLedgerAmount(cents: number): string {
    const formatted = formatAmount(Math.abs(cents), 'cents');
    return cents < 0 ? `-${formatted}` : formatted;
}

/**
 * A refund's fee-return rows are positive FEE/STRIPE_FEE entries (backend proportionally gives
 * back both on a refund — see webhooks.service.ts appendProportionalGiveBack) — labelled by sign
 * so they don't read as income. Invoice/payment-request income is distinguished via `refType`,
 * which `ReportSummary` (below, `bySource.PaymentRequest`) already models as a real backend-
 * recognized source.
 */
function formatKindLabel(row: LedgerEntryRow): string {
    if (row.kind === 'DONATION' && row.refType === 'PaymentRequest') {
        return 'Invoice';
    }
    if (row.amountCents > 0) {
        if (row.kind === 'FEE') return 'Platform fee returned';
        if (row.kind === 'STRIPE_FEE') return 'Processing fee returned';
    }
    return KIND_LABELS[row.kind] || row.kind;
}

const transactionColumns = [
    {
        id: 'occurredAt',
        label: 'Date',
        render: ({ data }: { data: LedgerEntryRow }) => `${dateFormat(data.occurredAt)} ${timeFormat(data.occurredAt, 'hh:mm A')}`,
    },
    {
        id: 'kind',
        label: 'Kind',
        render: ({ data }: { data: LedgerEntryRow }) => formatKindLabel(data),
    },
    {
        id: 'counterparty',
        label: 'Counterparty',
        render: ({ data }: { data: LedgerEntryRow }) => data.counterparty?.name || '—',
    },
    {
        id: 'status',
        label: 'Status',
        // A FEE/STRIPE_FEE row's status is the parent donation's, not its own — showing it reads
        // as though the fee itself has an independent refund/partial-refund state, which it doesn't.
        render: ({ data }: { data: LedgerEntryRow }) => (data.kind === 'FEE' || data.kind === 'STRIPE_FEE' ? '—' : data.status || '—'),
    },
    {
        id: 'amountCents',
        label: 'Amount',
        align: 'right' as const,
        render: ({ data }: { data: LedgerEntryRow }) => formatLedgerAmount(data.amountCents),
    },
];

/**
 * Parish financial reads (Chunk H backend). Transactions tab: CLERK+ (matches `RequireMerchant`
 * portal gate). Summary tab: MERCHANT_ADMIN+ only — hidden, not just disabled, mirroring the
 * Stripe Setup page's role-gated-inside-page pattern rather than a second route.
 */
export default function Reports() {
    const dispatch = useDispatch<AppDispatch>();
    const parishId = useSelector(selectSelectedParishId);
    const effectiveRole = useSelector(selectEffectiveStaffRole);
    const canViewSummary = hasAtLeastRole(effectiveRole, 'MERCHANT_ADMIN');

    const [tab, setTab] = useState<TabValue>('TRANSACTIONS');
    const [from, setFrom] = useState<Dayjs | null>(null);
    const [to, setTo] = useState<Dayjs | null>(null);

    // StyledTable's shared pagination widget — the single source of truth for page/pageSize
    // (see reportsSlice.ts: the slice deliberately keeps no second copy of either).
    const { pageNumber, pageSize } = useSelector((state: RootState) => state.app.table);

    const { items, total, loading, error } = useSelector((state: RootState) => state.reports.transactions);
    const { data: summary, loading: summaryLoading, error: summaryError } = useSelector((state: RootState) => state.reports.summary);

    const fromIso = from ? from.startOf('day').toISOString() : undefined;
    const toIso = to ? to.endOf('day').toISOString() : undefined;

    const resetTablePage = useCallback(() => {
        dispatch(updateTableState({ pageNumber: 0, pageSize, offset: 0 }));
    }, [dispatch, pageSize]);

    const loadTransactions = useCallback(
        (paging?: { pageNumber?: number; pageSize?: number }) => {
            if (!parishId) return;
            dispatch(
                fetchReportTransactions({
                    parishId,
                    page: (paging?.pageNumber ?? pageNumber) + 1,
                    pageSize: paging?.pageSize ?? pageSize,
                    from: fromIso,
                    to: toIso,
                })
            );
        },
        [dispatch, parishId, fromIso, toIso, pageNumber, pageSize]
    );

    const loadSummary = useCallback(() => {
        if (!parishId) return;
        dispatch(fetchReportsSummary({ parishId, from: fromIso, to: toIso }));
    }, [dispatch, parishId, fromIso, toIso]);

    // A parish switch (or role change) can drop MERCHANT_ADMIN while Summary is selected — fall back
    // to Transactions rather than leaving the card body blank (the "Summary" tab itself disappears).
    useEffect(() => {
        if (tab === 'SUMMARY' && !canViewSummary) {
            setTab('TRANSACTIONS');
            resetTablePage();
        }
    }, [tab, canViewSummary, resetTablePage]);

    // Parish switch: reset the shared pagination widget back to page 1 (mirrors Community.tsx /
    // Announcements.tsx) — a stale page N from the previous parish must never carry over.
    useEffect(() => {
        if (!parishId) return;
        if (pageNumber !== 0) {
            dispatch(updateTableState({ pageNumber: 0, pageSize, offset: 0 }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to parish switches
    }, [parishId, dispatch]);

    // Clear this screen's redux state on unmount so a later remount (possibly a different
    // parish) never flashes the previous parish's numbers before its own fetch resolves.
    useEffect(() => {
        return () => {
            dispatch(clearReports());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!parishId) return;
        // Wait for the pagination reset above (parish switch) to land before fetching page 1 —
        // otherwise this can fire once for the old parish's stale page N, immediately followed by
        // the reset's own re-fetch.
        if (pageNumber !== 0) return;
        if (tab === 'TRANSACTIONS') {
            loadTransactions();
        } else if (canViewSummary) {
            loadSummary();
        }
    }, [tab, parishId, fromIso, toIso, canViewSummary, pageNumber, pageSize, loadSummary, loadTransactions]);

    const rows = useMemo(() => items || [], [items]);

    const tabs = useMemo(
        () => [
            { value: 'TRANSACTIONS', label: 'Transactions' },
            ...(canViewSummary ? [{ value: 'SUMMARY' as TabValue, label: 'Summary' }] : []),
        ],
        [canViewSummary]
    );

    const handleTabChange = (v: TabValue) => {
        setTab(v);
        resetTablePage();
    };

    const handleFromChange = (value: Dayjs | null) => {
        setFrom(value);
        resetTablePage();
    };

    const handleToChange = (value: Dayjs | null) => {
        setTo(value);
        resetTablePage();
    };

    const handleClearDates = () => {
        setFrom(null);
        setTo(null);
        resetTablePage();
    };

    return (
        <Box sx={{ backgroundColor: '#F2F2F6', flex: 1, minHeight: '100%' }}>
            <Box sx={{ p: 2.75 }}>
                <Header />
            </Box>
            <Box sx={{ px: 2.75, pb: 3 }}>
                {!parishId && <Alert severity="warning">Select a parish to view reports.</Alert>}
                {parishId && (
                    <CardWrapper maxWidth={false} disableGutters>
                        <Typography sx={{ fontSize: { xs: 18, sm: 24 }, mb: 2, color: '#220E40', fontWeight: 500 }} variant="h1">
                            Reports
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Stack
                                direction={{ xs: 'column', lg: 'row' }}
                                justifyContent="space-between"
                                alignItems={{ xs: 'stretch', lg: 'center' }}
                                spacing={2}
                                sx={{ mb: 2 }}
                            >
                                <Box sx={{ minWidth: 0, flexShrink: 1 }}>
                                    <Tab tabs={tabs} tabValue={tab} setTabValue={handleTabChange} />
                                </Box>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={2}
                                    alignItems={{ xs: 'stretch', sm: 'center' }}
                                    sx={{ flexShrink: 0 }}
                                >
                                    <DatePicker
                                        label="From"
                                        value={from}
                                        onChange={handleFromChange}
                                        disableFuture
                                        maxDate={to ?? undefined}
                                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                        sx={{ width: { xs: '100%', sm: 170 } }}
                                    />
                                    <DatePicker
                                        label="To"
                                        value={to}
                                        onChange={handleToChange}
                                        disableFuture
                                        minDate={from ?? undefined}
                                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                        sx={{ width: { xs: '100%', sm: 170 } }}
                                    />
                                    <Button
                                        title="Clear"
                                        variant="outlined"
                                        onClick={handleClearDates}
                                        disabled={!from && !to}
                                        sx={{ width: { xs: '100%', sm: 100 }, height: 40 }}
                                    />
                                </Stack>
                            </Stack>
                        </LocalizationProvider>

                        {tab === 'TRANSACTIONS' && (
                            <>
                                {error && !loading && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {error}
                                    </Alert>
                                )}
                                <Table
                                    unStyled
                                    disableRowNavigation
                                    showLoading
                                    navigationHandler={() => {}}
                                    columns={transactionColumns}
                                    data={rows}
                                    totalCount={total}
                                    loading={loading}
                                    serverSidePagination
                                    loadData={loadTransactions}
                                />
                            </>
                        )}

                        {tab === 'SUMMARY' && canViewSummary && (
                            <Box sx={{ position: 'relative', minHeight: 200 }}>
                                <MuiLoader absolute loading={summaryLoading} />
                                {summaryError && !summaryLoading && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {summaryError}
                                    </Alert>
                                )}
                                {summary && (
                                    <Stack spacing={3}>
                                        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                                            <SummaryStat label="Net movement" value={formatLedgerAmount(summary.net)} />
                                            <SummaryStat label="Ledger entries" value={String(summary.entryCount)} />
                                            <SummaryStat
                                                label="Donations"
                                                value={formatLedgerAmount(summary.bySource.Donation.amountCents)}
                                            />
                                            <SummaryStat
                                                label="Payment requests"
                                                value={formatLedgerAmount(summary.bySource.PaymentRequest.amountCents)}
                                            />
                                        </Stack>
                                        <Typography variant="subtitle1" fontWeight={500}>
                                            By kind
                                        </Typography>
                                        <Grid container spacing={2}>
                                            {(Object.keys(KIND_LABELS) as LedgerKind[]).map((kind) => {
                                                const bucket = summary.byKind[kind];
                                                return (
                                                    <Grid item xs={12} sm={6} md={4} key={kind}>
                                                        <Card sx={{ p: 2 }}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {KIND_LABELS[kind]}
                                                            </Typography>
                                                            <Typography variant="h6">
                                                                {formatLedgerAmount(bucket?.amountCents ?? 0)}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {bucket?.count ?? 0} entries
                                                            </Typography>
                                                        </Card>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </Stack>
                                )}
                                {!summary && !summaryLoading && !summaryError && (
                                    <Typography variant="body2" color="text.secondary">
                                        No summary loaded yet.
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </CardWrapper>
                )}
            </Box>
        </Box>
    );
}

function SummaryStat({ label, value }: { label: string; value: string }) {
    return (
        <Card sx={{ p: 2, minWidth: 160 }}>
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="h6">{value}</Typography>
        </Card>
    );
}
